// IOSentra web API client.
//
// Talks to the cloud API (REST) and the auth-service (login/me/refresh/logout).
// Auth tokens are httpOnly cookies set by the auth-service, but we also keep
// the access token in memory for native/Capacitor-style Bearer usage and to
// drive a transparent refresh-on-401.

import axios, { type AxiosInstance } from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || '';

let accessToken: string | null = null;
let tenantId: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}
export function setTenantId(id: string | null): void {
  tenantId = id;
}

// Bridge from this (non-React) client to AuthContext: called once when a 401
// can't be recovered by a refresh (session truly over), so React can clear the
// user + show a "session expired" toast + fall back to the login screen.
let onSessionExpired: (() => void) | null = null;
export function setOnSessionExpired(cb: (() => void) | null): void {
  onSessionExpired = cb;
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // send/receive httpOnly auth cookies
});

api.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId;
  }
  // CSRF guard (E1): cookie-authenticated mutations must carry a Bearer token
  // OR X-Requested-With. When the session relies on the httpOnly cookie (no
  // in-memory accessToken), this header is what satisfies the guard. Harmless
  // on GETs.
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  return config;
});

// Transparent refresh on 401 (one retry).
let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = api
      .post('/api/v1/auth/refresh')
      .then((res) => {
        const token = (res.data as { accessToken?: string })?.accessToken;
        if (token) setAccessToken(token);
        return Boolean(token);
      })
      .catch(() => false)
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const url: string = original?.url ?? '';
    // Never try to refresh a 401 that came from the auth endpoints themselves —
    // that would recurse (a failing /auth/refresh triggering another refresh).
    // `me` is included because it's the cold-start session PROBE: a 401 there
    // just means "not signed in yet", so it must not fire the session-expired
    // toast on the login page of a fresh visitor.
    const isAuthEndpoint = /\/api\/v1\/auth\/(refresh|login|logout|me)/.test(url);
    if (error.response?.status === 401 && original && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      if (await tryRefresh()) {
        return api(original);
      }
      // Refresh genuinely failed → the session is over. Clear the in-memory
      // token and notify AuthContext (redirect to login + toast).
      setAccessToken(null);
      onSessionExpired?.();
    }
    return Promise.reject(error);
  },
);
